import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { global, globalMenu } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";

interface SnFileHelperProps {
  onFileUploadSuccess: (fileName: string, isUpload: boolean) => void; // 파일 업로드 성공 시 호출할 함수
  accept?: string;
}

const SnFileHelper = forwardRef(
  ({ onFileUploadSuccess, accept }: SnFileHelperProps, ref) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [customPath, setCustomPath] = useState<string>("");
    const cdModule = globalMenu.getState().activeMenuInfo.cd_module || "";

    useImperativeHandle(ref, () => ({
      downloadFile,
      deleteFile,
      openFileDialog,
    }));

    // 파일 업로드 함수
    const uploadFiles = async (files: File[], customPath: string) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("filePath", customPath);

      try {
        const response = await axios.post("/snotes/file/upload2", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
        });
        if (response.status === 200) {
          const savedName = response.data?.[0]?.nm_file || files[0].name;
          onFileUploadSuccess(savedName, true);
        } else {
        }
      } catch (error: any) {
        console.error("파일 업로드 오류:", error);
        await showDialog("error", "파일 업로드 오류: " + error.message);
      }
    };

    // 파일 다운로드 함수
    const downloadFile = async (fileName: string, customPath: string) => {
      try {
        if (!fileName) return;

        const params = {
          dc_url: customPath + fileName,
        };
        const res = await fetch("/snotes/file/download2", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
          body: JSON.stringify(params),
        });
        if (!res.ok) {
          let message = "다운로드 실패";
          try {
            const err = await res.json();
            if (err?.message) message = err.message;
          } catch {}
          throw new Error(message);
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error: any) {
        console.error("파일 다운로드 오류:", error);
        await showDialog("error", error.message);
      }
    };

    // 파일 삭제 함수. 삭제 성공 여부를 반환한다.
    const deleteFile = async (
      fileName: string,
      customPath: string
    ): Promise<boolean> => {
      try {
        if (!fileName) return true;

        const response = await axios.delete(
          `/snotes/file/delete2/${fileName}?filePath=${customPath}`,
          {
            withCredentials: true,
            headers: {
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
          }
        );
        if (response.status === 200 && typeof response.data === "string") {
          onFileUploadSuccess(fileName, false);
          return true;
        } else {
          await showDialog("error", response.data.result.message);
          return false;
        }
      } catch (error: any) {
        // 서버에 파일이 이미 없는 경우는 삭제된 것과 같으므로 성공으로 간주한다.
        if (error?.response?.status === 404) {
          return true;
        }
        console.error("파일 삭제 실패:", error);
        await showDialog("error", "파일 삭제 실패. 관리자에게 문의해주세요.");
        return false;
      }
    };

    // 파일 선택창 열기
    const openFileDialog = (path: string) => {
      setCustomPath(path);
      if (fileInputRef.current) {
        // 직전과 같은 파일을 다시 선택해도 onChange가 발생하도록 값을 비운다.
        fileInputRef.current.value = "";
      }
      fileInputRef.current?.click();
    };

    // 파일 변경 시 업로드
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        uploadFiles(Array.from(event.target.files), customPath);
      }
    };

    return (
      <input
        type="file"
        // multiple
        ref={fileInputRef}
        style={{ display: "none" }} // input은 숨기기
        onChange={handleFileChange} // 파일 선택 시 처리
        accept={accept || "*"}
      />
    );
  }
);

export default React.memo(SnFileHelper);
